/**
 * useExperienceAutoSave Hook
 *
 * Implements dual-tier auto-save (localStorage + server) and draft recovery
 * for the Experience Creation Wizard.
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Story: SCRUM-185 (US-1.27.1-04: Auto-Save & Draft Recovery)
 * Location: apps/web/src/hooks/experiences/useExperienceAutoSave.js
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import apiRequest from '../../services/api';

const LOCAL_STORAGE_KEY = 'experience_creation_draft';
const LOCAL_SAVE_DELAY = 30000; // 30 seconds
const SERVER_SAVE_DELAY = 60000; // 60 seconds

/**
 * Maps DB formula_type values back to wizard pricingModel values.
 * Used when loading pricing config from server for draft recovery.
 */
const FORMULA_TYPE_TO_WIZARD = {
  per_person_flat: 'per_person',
  flat_group: 'flat_group',
  base_additional: 'base_plus_per',
  base_covers_n: 'base_covers_n'
};

/**
 * SCRUM-826: Maps wizard formData fields to section type names and content keys.
 * Used for saving section data via Path B (SectionDataController) alongside experience saves.
 *
 * The backend SectionDataService now accepts section type names (not just UUIDs) in the URL,
 * resolving them to UUIDs internally. This avoids hardcoding environment-specific UUIDs.
 */
const SECTION_FIELD_MAP = {
  whatsIncluded: { sectionTypeName: 'whats_included', wrapKey: 'items' },
  highlights: { sectionTypeName: 'highlights', wrapKey: 'highlights' },
  whatToBring: { sectionTypeName: 'what_to_bring', wrapKey: 'items' },
};

/**
 * Reverse mapping: section type name → formData field name + content key.
 * Used during draft recovery to map server section data back to formData fields.
 */
const SECTION_TYPE_TO_FIELD = Object.fromEntries(
  Object.entries(SECTION_FIELD_MAP).map(([field, config]) => [
    config.sectionTypeName,
    { fieldName: field, wrapKey: config.wrapKey }
  ])
);

/**
 * useExperienceAutoSave Hook
 *
 * @param {Object} formData - Current form data to auto-save
 * @param {string|null} experienceId - Experience ID (null for new experience)
 * @param {boolean} enabled - Whether auto-save is enabled
 * @returns {Object} Auto-save state and functions
 */
export default function useExperienceAutoSave(formData, experienceId = null, enabled = true) {
  // State
  const [localSaveStatus, setLocalSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [serverSaveStatus, setServerSaveStatus] = useState('idle');
  const [lastLocalSave, setLastLocalSave] = useState(null);
  const [lastServerSave, setLastServerSave] = useState(null);
  const [error, setError] = useState(null);

  // Refs for debounce timers
  const localSaveTimer = useRef(null);
  const serverSaveTimer = useRef(null);
  const previousFormData = useRef(null);

  // SCRUM-826: Track last-saved section data for change detection (AC-04)
  const lastSavedSections = useRef({});

  /**
   * Save to localStorage
   */
  const saveToLocalStorage = useCallback((data) => {
    try {
      const draft = {
        formData: data,
        experienceId,
        savedAt: new Date().toISOString(),
        version: 1
      };

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(draft));
      setLocalSaveStatus('saved');
      setLastLocalSave(new Date());
      setError(null);

      // Reset status after 3 seconds
      setTimeout(() => setLocalSaveStatus('idle'), 3000);
    } catch (err) {
      console.error('Failed to save to localStorage:', err);
      setLocalSaveStatus('error');
      setError('Failed to save draft locally');
    }
  }, [experienceId]);

  /**
   * SCRUM-826: Save section data via Path B (SectionDataController).
   * Only saves sections that have changed since last save (AC-04).
   * Section save failures do not block or fail the experience save.
   *
   * @param {Object} formDataToSave - Current formData
   * @param {string} expId - Experience ID (must exist)
   */
  const saveSectionData = useCallback(async (formDataToSave, expId) => {
    if (!expId) { return; }

    const savePromises = [];

    for (const [fieldName, { sectionTypeName, wrapKey }] of Object.entries(SECTION_FIELD_MAP)) {
      const fieldValue = formDataToSave[fieldName];

      // Skip if no data or empty array
      if (!fieldValue || !Array.isArray(fieldValue) || fieldValue.length === 0) {
        continue;
      }

      // AC-04: Skip if unchanged since last save
      if (JSON.stringify(fieldValue) === JSON.stringify(lastSavedSections.current[fieldName])) {
        continue;
      }

      // Save via Path B: POST /api/v1/experiences/:id/sections/:sectionTypeName
      savePromises.push(
        apiRequest(`/api/v1/experiences/${expId}/sections/${sectionTypeName}`, {
          method: 'POST',
          body: JSON.stringify({
            contentData: { [wrapKey]: fieldValue },
            isDraft: true
          })
        }).then(() => {
          // Mark section as saved for change detection
          lastSavedSections.current[fieldName] = [...fieldValue];
        }).catch(() => {
          // Section save failure must not prevent experience save
        })
      );
    }

    if (savePromises.length > 0) {
      await Promise.allSettled(savePromises);
    }
  }, []);

  /**
   * Save to server
   */
  const saveToServer = useCallback(async (data) => {
    try {
      setServerSaveStatus('saving');

      const endpoint = experienceId
        ? `/api/v1/experiences/${experienceId}`
        : '/api/v1/experiences';

      const method = experienceId ? 'PUT' : 'POST';

      const response = await apiRequest(endpoint, {
        method,
        body: JSON.stringify({
          ...data,
          active: false // Save as draft
        })
      });

      // SCRUM-826: Save section data via Path B after experience save succeeds
      const expId = experienceId || (response && response.data && response.data.id);
      if (expId) {
        await saveSectionData(data, expId);
      }

      setServerSaveStatus('saved');
      setLastServerSave(new Date());
      setError(null);

      // Reset status after 3 seconds
      setTimeout(() => setServerSaveStatus('idle'), 3000);

      return response;
    } catch (err) {
      console.error('Failed to save to server:', err);

      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to save draft to server');
      }

      setServerSaveStatus('error');
      return null;
    }
  }, [experienceId, saveSectionData]);

  /**
   * Check if form data has changed
   */
  const hasDataChanged = useCallback((newData) => {
    if (!previousFormData.current) {return false;}
    return JSON.stringify(newData) !== JSON.stringify(previousFormData.current);
  }, []);

  /**
   * Trigger auto-save (debounced)
   */
  useEffect(() => {
    if (!enabled || !formData) {return;}

    // First render: initialize tracking without triggering save (SCRUM-973)
    // This prevents saving empty/initial form data on wizard mount.
    if (!previousFormData.current) {
      previousFormData.current = formData;
      return;
    }

    // Check if data has changed
    if (!hasDataChanged(formData)) {return;}

    // Update previous form data
    previousFormData.current = formData;

    // Clear existing timers
    if (localSaveTimer.current) {clearTimeout(localSaveTimer.current);}
    if (serverSaveTimer.current) {clearTimeout(serverSaveTimer.current);}

    // Set localStorage save status
    setLocalSaveStatus('saving');

    // Schedule localStorage save (30s)
    localSaveTimer.current = setTimeout(() => {
      saveToLocalStorage(formData);
    }, LOCAL_SAVE_DELAY);

    // Set server save status
    setServerSaveStatus('saving');

    // Schedule server save (60s)
    serverSaveTimer.current = setTimeout(() => {
      saveToServer(formData);
    }, SERVER_SAVE_DELAY);

    // Cleanup timers on unmount
    return () => {
      if (localSaveTimer.current) {clearTimeout(localSaveTimer.current);}
      if (serverSaveTimer.current) {clearTimeout(serverSaveTimer.current);}
    };
  }, [formData, enabled, hasDataChanged, saveToLocalStorage, saveToServer]);

  /**
   * Manually trigger save (for explicit "Save Draft" button)
   */
  const saveDraftNow = useCallback(async () => {
    // Clear any pending timers
    if (localSaveTimer.current) {clearTimeout(localSaveTimer.current);}
    if (serverSaveTimer.current) {clearTimeout(serverSaveTimer.current);}

    // Save immediately
    saveToLocalStorage(formData);
    const result = await saveToServer(formData);

    return result;
  }, [formData, saveToLocalStorage, saveToServer]);

  /**
   * Load draft from localStorage
   */
  const loadLocalDraft = useCallback(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!stored) {return null;}

      const draft = JSON.parse(stored);

      // Validate draft structure
      if (!draft.formData || !draft.savedAt) {
        console.warn('Invalid draft structure in localStorage');
        return null;
      }

      return draft;
    } catch (err) {
      console.error('Failed to load draft from localStorage:', err);
      return null;
    }
  }, []);

  /**
   * Load draft from server.
   * Also fetches pricing config to recover pricingModel selection.
   */
  const loadServerDraft = useCallback(async (draftId) => {
    try {
      const response = await apiRequest(`/api/v1/experiences/${draftId}`, {
        method: 'GET'
      });

      // SCRUM-826: Load section data from server for draft recovery (AC-05)
      try {
        const sectionsResponse = await apiRequest(
          `/api/v1/experiences/${draftId}/sections`,
          { method: 'GET' }
        );
        if (sectionsResponse.success && sectionsResponse.sections) {
          for (const section of sectionsResponse.sections) {
            const mapping = SECTION_TYPE_TO_FIELD[section.section_type_name];
            if (mapping && section.content_data) {
              const items = section.content_data[mapping.wrapKey];
              if (items && Array.isArray(items)) {
                response[mapping.fieldName] = items;
                // Sync change detection so recovered data isn't re-saved immediately
                lastSavedSections.current[mapping.fieldName] = [...items];
              }
            }
          }
        }
      } catch (_sectionsErr) {
        // Sections may not exist yet (404) — not an error
      }

      // Fetch pricing config to recover pricingModel (SCRUM-824)
      try {
        const pricingResponse = await apiRequest(
          `/api/v1/guides/experiences/${draftId}/pricing/config`,
          { method: 'GET' }
        );
        if (pricingResponse.success && pricingResponse.data) {
          const formulaType = pricingResponse.data.pricing_model_type;
          if (formulaType && FORMULA_TYPE_TO_WIZARD[formulaType]) {
            response.pricingModel = FORMULA_TYPE_TO_WIZARD[formulaType];
          }
        }
      } catch (_pricingErr) {
        // Pricing config may not exist yet (404) — not an error
      }

      return response;
    } catch (err) {
      console.error('Failed to load draft from server:', err);
      return null;
    }
  }, []);

  /**
   * Clear local draft
   */
  const clearLocalDraft = useCallback(() => {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setLastLocalSave(null);
    } catch (err) {
      console.error('Failed to clear localStorage:', err);
    }
  }, []);

  /**
   * Get most recent draft (compare localStorage vs server)
   */
  const getMostRecentDraft = useCallback(async () => {
    const localDraft = loadLocalDraft();

    // If editing existing experience, check server
    if (experienceId) {
      const serverDraft = await loadServerDraft(experienceId);

      // Compare timestamps if both exist
      if (localDraft && serverDraft) {
        const localTime = new Date(localDraft.savedAt).getTime();
        const serverTime = new Date(serverDraft.updated_at || serverDraft.created_at).getTime();

        return localTime > serverTime ? localDraft : serverDraft;
      }

      return serverDraft || localDraft;
    }

    return localDraft;
  }, [experienceId, loadLocalDraft, loadServerDraft]);

  /**
   * Format last save time
   */
  const formatLastSaveTime = (date) => {
    if (!date) {return null;}
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Computed save status display
  const getSaveStatusDisplay = () => {
    // Prioritize server status since it's more important
    if (serverSaveStatus === 'saving') {
      return { text: 'Saving to server...', color: 'text-gray-600' };
    }
    if (serverSaveStatus === 'saved') {
      return {
        text: `Saved at ${formatLastSaveTime(lastServerSave)}`,
        color: 'text-green-600'
      };
    }
    if (serverSaveStatus === 'error') {
      return { text: 'Failed to save to server', color: 'text-red-600' };
    }

    // Fall back to local status
    if (localSaveStatus === 'saving') {
      return { text: 'Saving locally...', color: 'text-gray-600' };
    }
    if (localSaveStatus === 'saved') {
      return {
        text: `Saved locally at ${formatLastSaveTime(lastLocalSave)}`,
        color: 'text-green-600'
      };
    }
    if (localSaveStatus === 'error') {
      return { text: 'Failed to save locally', color: 'text-red-600' };
    }

    return { text: '', color: '' };
  };

  const isSaving = localSaveStatus === 'saving' || serverSaveStatus === 'saving';
  const hasSaved = localSaveStatus === 'saved' || serverSaveStatus === 'saved';
  const hasError = localSaveStatus === 'error' || serverSaveStatus === 'error';

  return {
    // Status
    isSaving,
    hasSaved,
    hasError,
    error,
    localSaveStatus,
    serverSaveStatus,
    lastLocalSave,
    lastServerSave,

    // Functions
    saveDraftNow,
    loadLocalDraft,
    loadServerDraft,
    getMostRecentDraft,
    clearLocalDraft,

    // Display
    saveStatusDisplay: getSaveStatusDisplay()
  };
}
